/**
 * How a document's Form tab is laid out, and what its line rows must carry.
 *
 * The form is a column of blocks — header sections, line grids, the Total
 * Value card, the panels a page hands in — and the reference does not always
 * put every section above every grid: an RFQ's Additional Info sits between
 * its two grids, a project's billing terms sit inside Billing Details. The
 * order is worked out here, once, from `SectionDef.placement`,
 * `LineGridDef.afterSection` and `DocumentDef.formOrder`, so the screen and
 * anything else that walks the form agree.
 */
import type { DocumentDef, LineColumnDef, LineGridDef, RowCondition, SectionDef } from './types.js'

/** A grid's key on the form: its own `key`, else its storage section. */
export const gridKeyOf = (grid: LineGridDef): string => grid.key ?? grid.section ?? 'ITEM'

export type FormBlock =
  | { readonly kind: 'section'; readonly key: string; readonly section: SectionDef }
  /** The primary grid, `def.lines`. */
  | { readonly kind: 'lines'; readonly key: string; readonly grid: LineGridDef }
  | { readonly kind: 'grid'; readonly key: string; readonly grid: LineGridDef }
  | { readonly kind: 'totalValue'; readonly key: 'totalValue' }
  | { readonly kind: 'projectFigures'; readonly key: 'projectFigures' }
  /** A panel the page supplies — `slot:projectTeam`. Only ever where `formOrder` names it. */
  | { readonly kind: 'slot'; readonly key: string; readonly name: string }

/**
 * The Form tab's blocks, in order.
 *
 * Without a `formOrder`: the sections (each followed by any grid that names
 * it in `afterSection`), then the primary grid, then the `afterLines`
 * sections, then the remaining grids, then the totals. With one, the named
 * blocks come first in that order and everything it does not name follows in
 * the default order — so a stale order can misplace a block, never lose it.
 *
 * `sections` defaults to the definition's; the form passes its own when it
 * has added a field (the typed document number).
 */
export const formBlocks = (
  def: Pick<DocumentDef, 'form' | 'lines' | 'grids' | 'formOrder'>,
  sections: readonly SectionDef[] = def.form.sections,
): FormBlock[] => {
  // A nested grid is drawn inside its parent's rows, never as a block (PROJ1-008).
  const grids = (def.grids ?? []).filter((g) => g.nested !== true)
  const sectionKeys = new Set(sections.map((s) => s.key))
  const placed = (g: LineGridDef): boolean => g.afterSection !== undefined && sectionKeys.has(g.afterSection)

  const defaults: FormBlock[] = []
  const withGridsAfter = (s: SectionDef): void => {
    defaults.push({ kind: 'section', key: s.key, section: s })
    if (def.lines && def.lines.afterSection === s.key) {
      defaults.push({ kind: 'lines', key: `grid:${gridKeyOf(def.lines)}`, grid: def.lines })
    }
    for (const g of grids) if (g.afterSection === s.key) defaults.push({ kind: 'grid', key: `grid:${gridKeyOf(g)}`, grid: g })
  }
  for (const s of sections) if (s.placement !== 'afterLines') withGridsAfter(s)
  if (def.lines && !placed(def.lines)) defaults.push({ kind: 'lines', key: `grid:${gridKeyOf(def.lines)}`, grid: def.lines })
  for (const s of sections) if (s.placement === 'afterLines') withGridsAfter(s)
  for (const g of grids) if (!placed(g)) defaults.push({ kind: 'grid', key: `grid:${gridKeyOf(g)}`, grid: g })
  defaults.push({ kind: 'totalValue', key: 'totalValue' }, { kind: 'projectFigures', key: 'projectFigures' })

  if (!def.formOrder || def.formOrder.length === 0) return defaults

  const byKey = new Map(defaults.map((b) => [b.key, b]))
  const out: FormBlock[] = []
  const used = new Set<string>()
  for (const key of def.formOrder) {
    if (used.has(key)) continue
    if (key.startsWith('slot:')) {
      out.push({ kind: 'slot', key, name: key.slice('slot:'.length) })
      used.add(key)
      continue
    }
    const block = byKey.get(key)
    if (!block) continue
    out.push(block)
    used.add(key)
  }
  for (const block of defaults) if (!used.has(block.key)) out.push(block)
  return out
}

/** Does this row's cell hold one of the condition's values (or anything, for `isSet`)? */
export const rowHolds = (c: RowCondition, row: Readonly<Record<string, unknown>>): boolean => {
  const raw = row[c.column]
  const v = raw === null || raw === undefined ? '' : String(raw)
  return c.isSet ? v !== '' : (c.equals ?? []).includes(v)
}

/** Is this column shown on this row — its `visibleWhen` — in the grid cell and the row dialog? */
export const isLineCellVisible = (column: LineColumnDef, row: Readonly<Record<string, unknown>>): boolean =>
  !column.visibleWhen || rowHolds(column.visibleWhen, row)

/** Does this cell read "-" on this row — not a question here (`dashWhen`)? */
export const isLineCellDashed = (column: LineColumnDef, row: Readonly<Record<string, unknown>>): boolean =>
  column.dashWhen !== undefined && rowHolds(column.dashWhen, row)

/** The cell's label on this row, in the row dialog (`labelsBy`). */
export const lineCellLabel = (column: LineColumnDef, row: Readonly<Record<string, unknown>>): string => {
  if (!column.labelsBy) return column.label
  const by = row[column.labelsBy.column]
  return column.labelsBy.labels[by === null || by === undefined ? '' : String(by)] ?? column.label
}

/** A stored value that counts as something: not blank, and not a zero figure. */
const holdsValue = (raw: unknown): boolean => {
  if (raw === null || raw === undefined) return false
  const text = String(raw).trim()
  if (text === '') return false
  return !(/^-?[\d.]+$/.test(text) && Number(text) === 0)
}

/**
 * A grid's columns as the header answers have them (`whenHeader`): a Single
 * Rate project's supply columns gone and its installation rate read "Rate".
 * A column is only dropped while no row holds a value in it, so a figure
 * stored before the answer changed is still on screen to be seen and cleared.
 */
export const columnsForHeader = (
  columns: readonly LineColumnDef[],
  header: Readonly<Record<string, unknown>>,
  rows: ReadonlyArray<Readonly<Record<string, unknown>>> = [],
): LineColumnDef[] => columns.flatMap((column) => {
  const when = column.whenHeader
  if (!when) return [column]
  const answer = header[when.field]
  if (!when.equals.includes(answer === null || answer === undefined ? '' : String(answer))) return [column]
  if (when.hidden && !rows.some((r) => holdsValue(r[column.key]))) return []
  return [when.label ? { ...column, label: when.label } : column]
})

/** Is this cell open for typing on this row: editable, shown, and not held shut by another cell? */
export const isLineCellOpen = (column: LineColumnDef, row: Readonly<Record<string, unknown>>): boolean => {
  if (column.editable !== true) return false
  if (!isLineCellVisible(column, row)) return false
  if (isLineCellDashed(column, row)) return false
  if (column.editableWhen && !rowHolds(column.editableWhen, row)) return false
  if (column.lockedWhen && rowHolds(column.lockedWhen, row)) return false
  return true
}

export interface MissingLineCell {
  /** The grid's title — "BOQ and Estimation". */
  readonly grid: string
  /** 1-based, as the grid numbers it. */
  readonly row: number
  readonly column: string
  /** "BOQ and Estimation, line 3: Internal ref is required." */
  readonly message: string
}

/**
 * The required line cells left empty (`LineColumnDef.required`), grid by grid.
 *
 * Only a cell the operator could have filled counts: one hidden on that row
 * by `visibleWhen`, or held shut by `editableWhen`/`lockedWhen`, is not asked
 * for. Rows are matched to grids by section, and for two grids over one
 * section by the grid's `rows` test, as the form splits them.
 */
export const missingLineCells = (
  def: Pick<DocumentDef, 'lines' | 'grids'>,
  lines: ReadonlyArray<Readonly<Record<string, unknown>>>,
): MissingLineCell[] => {
  const primary = def.lines?.section ?? 'ITEM'
  const grids = [...(def.lines ? [def.lines] : []), ...(def.grids ?? [])]
  const out: MissingLineCell[] = []
  for (const grid of grids) {
    const required = grid.columns.filter((c) => c.required === true)
    if (required.length === 0) continue
    const section = grid.section ?? 'ITEM'
    const rows = lines.filter((l) => String(l['section'] ?? primary) === section)
      .filter((l) => !grid.rows || rowHolds({ column: grid.rows.column, isSet: true }, l) === grid.rows.isSet)
    rows.forEach((row, index) => {
      for (const column of required) {
        if (!isLineCellOpen(column, row)) continue
        const raw = row[column.key]
        if (raw !== null && raw !== undefined && String(raw).trim() !== '') continue
        out.push({
          grid: grid.title, row: index + 1, column: column.label,
          message: `${grid.title}, line ${index + 1}: ${column.label} is required.`,
        })
      }
    })
  }
  return out
}
